// Per-shelf sequence is the invariant; never mean-x.
import { itemsWithLayout } from "../verticalBlockingFix";
import { GeneratedLayout, StudioItem } from "../studioTypes";

/** Empty means the declared order is exactly the previous order plus the sanctioned move. */
export const declaredOrderViolations = (
  previous: readonly string[],
  next: readonly string[],
  travelling: readonly string[],
): string[] => {
  const skip = new Set(travelling);
  const want = previous.filter((key) => !skip.has(key));
  const got = next.filter((key) => !skip.has(key) && want.includes(key));
  if (want.join("\u0000") === got.join("\u0000")) return [];
  const at = want.findIndex((key, index) => got[index] !== key);
  return [`declared order departs from the previous order at "${want[at] ?? got[want.length]}"`];
};

export interface LaneOrderOptions {
  /** Declared rank per block key; unranked keys are excluded from the lane sequence. */
  rank: ReadonlyMap<string, number>;
  /** Per-run rank override (a cross-run block ranks differently per run); wins over `rank`. */
  rankOf?: (run: number, blockKey: string) => number;
  blockKeyOf: (item: StudioItem) => string;
}

export interface LaneOrderFinding {
  laneKey: string;
  blockKey: string;
  kind: "interleaved" | "out-of-order";
}

/** Per lane, each block must appear once and in declared-rank order. Real plans carry baked-in disorder, so callers diff these findings against a baseline: only NEW disorder is the move's. */
export const laneOrderFindings = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  options: LaneOrderOptions,
): LaneOrderFinding[] => {
  const { rank, blockKeyOf } = options;
  const findings: LaneOrderFinding[] = [];
  const lanes = new Map<string, { key: string; xCm: number }[]>();
  for (const item of itemsWithLayout(items, layout)) {
    const key = blockKeyOf(item);
    for (const placement of item.placements) {
      const laneKey = `${placement.run}|${placement.shelf}`;
      const lane = lanes.get(laneKey) ?? [];
      lane.push({ key, xCm: placement.xCm });
      lanes.set(laneKey, lane);
    }
  }
  for (const [laneKey, lane] of [...lanes.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const laneRun = Number(laneKey.split("|")[0]);
    const rankAt = (key: string): number =>
      options.rankOf?.(laneRun, key) ?? rank.get(key) ?? Number.MAX_SAFE_INTEGER;
    lane.sort((a, b) => a.xCm - b.xCm);
    // Unranked blocks are outside the declared contract; contiguity guards them instead.
    const sequence: string[] = [];
    for (const entry of lane) {
      if (rankAt(entry.key) === Number.MAX_SAFE_INTEGER) continue;
      if (sequence[sequence.length - 1] !== entry.key) sequence.push(entry.key);
    }
    const seen = new Set<string>();
    let interleaved: string | null = null;
    for (const key of sequence) {
      if (seen.has(key)) {
        interleaved = key;
        break;
      }
      seen.add(key);
    }
    if (interleaved !== null) {
      findings.push({ laneKey, blockKey: interleaved, kind: "interleaved" });
      continue;
    }
    const sorted = [...sequence].sort((a, b) => rankAt(a) - rankAt(b));
    if (sequence.join("\u0000") !== sorted.join("\u0000")) {
      const at = sequence.findIndex((key, index) => sorted[index] !== key);
      findings.push({ laneKey, blockKey: sequence[at], kind: "out-of-order" });
    }
  }
  return findings;
};

/** String rendering of laneOrderFindings, for reports and tests. */
export const laneOrderViolations = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  options: LaneOrderOptions,
): string[] =>
  laneOrderFindings(layout, items, options).map((finding) =>
    finding.kind === "interleaved"
      ? `lane ${finding.laneKey} interleaves "${finding.blockKey}"`
      : `lane ${finding.laneKey} is out of declared order at "${finding.blockKey}"`,
  );
